/** @file NodeTree: the puppet's node hierarchy (search, multi-select, rename, frame, badges, eye, keys). */
import type { ReactElement } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useStore } from 'zustand';
import { useEditor } from '../../state/useEditor';
import { useEditorStore } from '../../state/useEditorStore';
import { clickSelection } from './clickSelection';
import { frameNode } from './frameNode';
import { groupCounts } from './groupCounts';
import { keyedNodes } from './keyedNodes';
import { matchTreeSearch } from './matchTreeSearch';
import { NodeRow } from './NodeRow';
import type { NodeRowActions } from './NodeRow';
import { parseTreeSearch } from './parseTreeSearch';
import { renameNode } from './renameNode';
import { reparentNode } from './reparentNode';
import { treeBindings } from './treeBindings';
import { treeHandlers } from './treeHandlers';
import { treeMenu } from './treeMenu';
import { treeRows } from './treeRows';
import type { TreeRow } from './treeRows';
import { TreeSearch } from './TreeSearch';
import { treeUi } from './treeUi';
import { usePanelKeys } from './usePanelKeys';

const confirmBox = (text: string): boolean => window.confirm(text);

/** Rows from `puppet.doc.puppet.nodes`; highlights `selection.nodes`; registers `treeBindings` in context `tree`. */
export function NodeTree(): ReactElement {
  const store = useEditorStore();
  const ui = treeUi(store);
  const puppet = useEditor((s) => s.puppet.doc?.puppet ?? null);
  const clip = useEditor((s) => (s.playhead.clip === null ? undefined : s.puppet.doc?.clips[s.playhead.clip]));
  const frame = useEditor((s) => s.playhead.frame);
  const selected = useEditor((s) => s.selection.nodes);
  const search = useStore(ui, (s) => s.search);
  const groups = useStore(ui, (s) => s.groups);
  const viewHidden = useStore(ui, (s) => s.viewHidden);
  const renaming = useStore(ui, (s) => s.renaming);
  const renameError = useStore(ui, (s) => s.renameError);
  const [collapsed, setCollapsedSet] = useState<ReadonlySet<string>>(() => new Set());
  const rows = useMemo(() => {
    if (puppet === null) return [];
    return treeRows(puppet.nodes, { collapsed, sockets: puppet.sockets, visible: matchTreeSearch(puppet, parseTreeSearch(search), groups) });
  }, [puppet, collapsed, search, groups]);
  const rowsRef = useRef<TreeRow[]>(rows);
  rowsRef.current = rows;
  const setCollapsed = useCallback((name: string, fold: boolean) => {
    setCollapsedSet((prev) => {
      const next = new Set(prev);
      if (fold) next.add(name); else next.delete(name);
      return next;
    });
  }, []);
  const handlers = useMemo(() => treeHandlers(store, { rows: () => rowsRef.current, setCollapsed, confirm: confirmBox }), [store, setCollapsed]);
  const keyProps = usePanelKeys('tree', treeBindings, handlers);
  const drawablesOf = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const d of puppet?.drawables ?? []) map.set(d.node, [...(map.get(d.node) ?? []), d.id]);
    return map;
  }, [puppet]);
  const keyed = useMemo(() => keyedNodes(clip, frame), [clip, frame]);
  const counts = useMemo(() => groupCounts(puppet?.nodes ?? {}), [puppet]);
  const actions: NodeRowActions = {
    onSelect: (name, mods) => {
      const next = clickSelection(rows.map((r) => r.name), selected, ui.getState().anchor, name, mods);
      ui.getState().setAnchor(next.anchor);
      store.getState().select({ nodes: next.nodes, drawable: null });
    },
    onMenu: (name, x, y) => { store.getState().openMenu(x, y, treeMenu(store, name, confirmBox)); },
    onToggle: (n) => { setCollapsed(n, !collapsed.has(n)); },
    onReparent: (name, parent) => { reparentNode(store, name, parent); },
    onFrame: (name) => { frameNode(store, name); },
    onEye: (name) => { ui.getState().toggleViewHidden(name); },
    onRename: async (name, value, viaBlur) => {
      const error = await renameNode(store, name, value);
      if (error === null) { ui.getState().stopRename(); return true; }
      if (!viaBlur) { ui.getState().setRenameError(error); return false; }
      ui.getState().stopRename();
      store.getState().notify('warning', `Rename of ${name} cancelled: ${error}`);
      return true;
    },
    onRenameCancel: () => { ui.getState().stopRename(); },
  };
  if (puppet === null) return <div data-panel="nodeTree" style={{ padding: 8, color: 'var(--muted, #8b919e)' }}>No puppet open</div>;
  const hiddenAbove = (name: string): boolean => {
    for (let at = puppet.nodes[name]?.parent; at !== undefined; at = puppet.nodes[at]?.parent) if (viewHidden.has(at)) return true;
    return false;
  };
  return (
    <div data-panel="nodeTree" {...keyProps}
      style={{ display: 'flex', flexDirection: 'column', height: '100%', fontSize: 12, outline: 'none', fontFamily: 'var(--font-sans, system-ui, sans-serif)' }}>
      <TreeSearch counts={counts} />
      <div role="tree" aria-label="Nodes" aria-multiselectable style={{ overflow: 'auto', flex: 1 }}>
        {rows.map((row) => (
          <NodeRow key={row.name} actions={actions} view={{
            row, selected: selected.includes(row.name), collapsed: collapsed.has(row.name), drawables: drawablesOf.get(row.name) ?? [],
            keyed: keyed.has(row.name), frame, viewHidden: viewHidden.has(row.name), viewHiddenAbove: hiddenAbove(row.name),
            renaming: renaming === row.name, renameError: renaming === row.name ? renameError : null,
          }} />
        ))}
        {rows.length === 0 && <div style={{ padding: 8, color: 'var(--muted, #8b919e)' }}>No node matches</div>}
      </div>
    </div>
  );
}
